import { Button, Pill } from '@/shared/components';
import type { ResourceOccupancyEntry, ResourceOccupancyRow } from '@/shared/api';
import { clockFromMinutes } from '../resourceModel';
import {
  BLOCK_REASON_LABEL,
  ENTRY_STATE_LABEL,
  clarificationText,
  entryState,
  type EntryState,
} from './resourceCalendarModel';
import { paymentText } from './resourcePaymentModel';
import { ResourceTimeStrip } from './ResourceTimeStrip';

const PILL: Record<EntryState, 'live' | 'warning' | 'stop' | 'default'> = {
  reserved: 'live',
  held: 'warning',
  blocked: 'warning',
  clarification: 'stop',
  cancelled: 'default',
};

/**
 * Eine Ressource an einem Tag als Karte: Tagesliste unter dem Raster, am Handy die ganze Ansicht
 * (dann mit Band als Übersicht). Jede Belegung steht mit Text und Aktion da, nicht nur als Farbe.
 */
export function ResourceDayRow({
  row,
  date,
  strip,
  timezone,
  canManage,
  busyId,
  onBlock,
  onCancel,
}: {
  row: ResourceOccupancyRow;
  date: string;
  /** Ohne Raster (Handy): Band als Übersicht und „Zeit sperren“ an der Karte; sonst trägt beides
   *  das Raster mit seiner Leiste. */
  strip: boolean;
  timezone: string;
  canManage: boolean;
  busyId: string | null;
  onBlock: () => void;
  onCancel: (entry: ResourceOccupancyEntry) => void;
}) {
  const entries = row.entries.filter((entry) => entry.date === date);
  return (
    <section
      aria-label={row.name}
      className="border-line/50 bg-card shadow-card rounded-lg border p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-ink min-w-0 truncate font-serif text-lg" title={row.name}>
          {row.name}
          {!row.isActive && <span className="text-ink-soft ml-2 text-sm">(deaktiviert)</span>}
        </h3>
        {strip && canManage && row.isActive && (
          <Button type="button" variant="ghost" size="sm" className="min-h-11" onClick={onBlock}>
            Zeit sperren
          </Button>
        )}
      </div>
      {strip && (
        <div className="mt-2">
          <ResourceTimeStrip row={row} date={date} />
        </div>
      )}
      {entries.length === 0 ? (
        <p className="text-ink-soft mt-3 text-sm">An diesem Tag frei.</p>
      ) : (
        <ul className="divide-line/60 mt-3 divide-y">
          {entries.map((entry) => (
            <EntryLine
              key={entry.id}
              entry={entry}
              timezone={timezone}
              canManage={canManage}
              busy={busyId === entry.id}
              onCancel={() => onCancel(entry)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function EntryLine({
  entry,
  timezone,
  canManage,
  busy,
  onCancel,
}: {
  entry: ResourceOccupancyEntry;
  timezone: string;
  canManage: boolean;
  busy: boolean;
  onCancel: () => void;
}) {
  const state = entryState(entry);
  const money = paymentText(entry, timezone);
  const who =
    entry.kind === 'Block'
      ? [entry.blockReason && BLOCK_REASON_LABEL[entry.blockReason], entry.note]
          .filter(Boolean)
          .join(' · ')
      : [entry.guestName, entry.bookingReference].filter(Boolean).join(' · ') || 'Gastreservierung';
  return (
    <li className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 py-2">
      <span className="text-ink whitespace-nowrap text-sm font-semibold tabular-nums">
        {clockFromMinutes(entry.startMinute)}–{clockFromMinutes(entry.endMinute)}
      </span>
      <Pill variant={PILL[state]}>{ENTRY_STATE_LABEL[state]}</Pill>
      <span
        className="text-ink-soft min-w-0 flex-1 truncate text-sm max-sm:order-last max-sm:basis-full"
        title={who}
      >
        {who}
      </span>
      {entry.clarificationReason && state === 'clarification' && (
        <p className="text-sealwax order-last basis-full text-sm">
          {clarificationText(entry.clarificationReason)}
        </p>
      )}
      {money && <p className="text-ink-soft order-last basis-full text-sm">{money}</p>}
      {canManage && state !== 'cancelled' && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="min-h-11"
          loading={busy}
          aria-label={`${ENTRY_STATE_LABEL[state]} ${clockFromMinutes(entry.startMinute)} aufheben`}
          onClick={onCancel}
        >
          Aufheben
        </Button>
      )}
    </li>
  );
}
